import { Component, input } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { NgClass } from '@angular/common';

import { AvailableIcons, ApIconComponent } from '@ap/icon';

import { ButtonLinkAlignment, ButtonLinkVariant } from './link.model';

/**
 * Renders an anchor tag styled as a button
 */
@Component({
    selector: 'ap-button-link',
    templateUrl: './link.component.html',
    styleUrls: ['./link.component.scss'],
    imports: [NgClass, ApIconComponent]
})
export class ApButtonLinkComponent {
    /**
     * Type of the button.
     * Options are 'primary' (default), 'secondary', 'skip-link' and 'edit'.
     */
    public readonly variant = input<ButtonLinkVariant>('primary');

    /**
     * Specifies where to open the linked document
     *
     * `_blank` - Opens the linked document in a new window or tab
     *
     * `_self` - Opens the linked document in the same frame as it was clicked (this is default)
     *
     * `_parent` - Opens the linked document in the parent frame
     *
     * `_top` - Opens the linked document in the full body of the window
     *
     * `framename` - Opens the linked document in the named iframe
     */
    public readonly target = input<string>(undefined);

    /**
     * Hints at the linked URL's format with a [MIME type](https://developer.mozilla.org/en-US/docs/Glossary/MIME_type)
     */
    public readonly type = input<string>(undefined);

    /**
     * Specifies the relationship between the current document and the linked document
     */
    public readonly rel = input<string>(undefined);

    /**
     * A space-separated list of URLs.
     * When the link is followed, the browser will send POST requests with the body PING to the URLs.
     * Typically for tracking.
     */
    public readonly ping = input<string>(undefined);

    /**
     * Specifies the url of the page the link goes to
     */
    public readonly href = input<string | SafeUrl>(undefined);

    /**
     * Adds a specified icon left of the button text (see ap-icon for icon options)
     */
    public readonly iconName = input<AvailableIcons>(undefined);

    /**
     * Specifies the language of the linked document
     */
    public readonly hreflang = input<string>(undefined);

    /**
     * specifies that the target will be downloaded when a user clicks on the hyperlink
     */
    public readonly download = input<string>(undefined);

    /**
     * Sets the button to full-width, which can be required in combination with the grid.
     */
    public readonly wide = input(false);

    /**
     * Sets the alignment of the button contents a.k.a justify-content
     */
    public readonly alignment = input<ButtonLinkAlignment>('center');
}
